import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings, getPageContent, savePageContent, getAllPages } from '../firebase/firebase-config';
import '../styles/dashboard.css';

// Import all editors
import HomeEditor from './editors/HomeEditor';
import AboutEditor from './editors/AboutEditor';
import AcademicsEditor from './editors/AcademicsEditor';
import CurriculumEditor from './editors/CurriculumEditor';
import CalendarEditor from './editors/CalendarEditor';
import SyllabusEditor from './editors/SyllabusEditor';
import QuestionPapersEditor from './editors/QuestionPapersEditor';
import HolidayHomeworkEditor from './editors/HolidayHomeworkEditor';
import TimeTableEditor from './editors/TimeTableEditor';
import TeachersEditor from './editors/TeachersEditor';
import GalleryEditor from './editors/GalleryEditor';
import NoticesEditor from './editors/NoticesEditor';
import SportsEditor from './editors/SportsEditor';
import ContactEditor from './editors/ContactEditor';
import MandatoryDisclosureEditor from './editors/MandatoryDisclosureEditor';
import DefaultEditor from './editors/DefaultEditor';

// Import settings components
import HeaderSettings from './settings/HeaderSettings';
import GlobalBannerSettings from './settings/GlobalBannerSettings';
import FooterSettings from './settings/FooterSettings';
import ColorSettings from './settings/ColorSettings';

function Dashboard() {
  const [activeTab, setActiveTab] = useState('header');
  const [activePage, setActivePage] = useState('home');
  const [settings, setSettings] = useState({
    schoolName: 'MIS International School',
    logo: '',
    favicon: '',
    primaryColor: '#1a3a6b',
    secondaryColor: '#f59e0b',
    headerBgColor: '#1a3a6b',
    footerBgColor: '#0f2244',
    bodyBgColor: '#ffffff',
    headerTextColor: '#ffffff',
    footerTextColor: '#ffffff',
    address: '123 Education Lane, Knowledge City, India 226001',
    phone: '+91 522 123 4567',
    email: 'info@mis-school.edu',
    googleMapEmbedUrl: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    copyright: '© 2026 MIS International School. All Rights Reserved.',
    navLinks: [],
    globalBannerImages: [],
    globalBannerVideo: '',
    useVideoBanner: false,
    footerLogo: ''
  });
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingField, setUploadingField] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [newPageName, setNewPageName] = useState('');
  const [showNewPageModal, setShowNewPageModal] = useState(false);

  const [pages, setPages] = useState({
    home: { name: 'Home', icon: '🏠', description: 'Main homepage with slider, notices, achievements' },
    about: { name: 'About Us', icon: '🏫', description: 'School information' },
    academics: { name: 'Academics', icon: '📚', description: 'Academics overview with links' },
    curriculum: { name: 'Curriculum', icon: '📖', description: 'CBSE Curriculum with PDF/images' },
    calendar: { name: 'Calendar', icon: '📅', description: 'Academic Calendar' },
    syllabus: { name: 'Syllabus', icon: '📋', description: 'Class-wise syllabus download' },
    questionPapers: { name: 'Question Papers', icon: '📝', description: 'Previous year papers' },
    holidayHomework: { name: 'Holiday Homework', icon: '🎒', description: 'Holiday homework' },
    timeTable: { name: 'Time Table', icon: '⏰', description: 'Class-wise timetable' },
    teachers: { name: 'Teachers', icon: '👨‍🏫', description: 'Staff directory' },
    gallery: { name: 'Gallery', icon: '🖼️', description: 'Photos and videos' },
    notices: { name: 'Notices', icon: '📢', description: 'School notices' },
    sports: { name: 'Sports', icon: '⚽', description: 'Sports programs' },
    contact: { name: 'Contact', icon: '📞', description: 'Contact information' },
    mandatoryDisclosure: { name: 'Mandatory Disclosure', icon: '⚖️', description: 'CBSE compliance' }
  });

  useEffect(() => { loadData(); }, [activePage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, pageData] = await Promise.all([
        getSettings(),
        activePage ? getPageContent(activePage) : Promise.resolve({})
      ]);
      setSettings(s => ({ ...s, ...settingsData }));
      setPageContent(pageData || {});
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const cleanFirebaseKey = (str) => {
    if (!str) return 'empty';
    return str.toString().replace(/[.#$/[\]/]/g, '_').replace(/\s+/g, '_').trim();
  };

  const cleanTimetableData = (timetable) => {
    if (!timetable) return {};
    const cleaned = {};
    Object.keys(timetable).forEach(className => {
      const cc = cleanFirebaseKey(className);
      cleaned[cc] = {};
      Object.keys(timetable[className] || {}).forEach(section => {
        const cs = cleanFirebaseKey(section);
        cleaned[cc][cs] = {};
        Object.keys(timetable[className][section] || {}).forEach(period => {
          cleaned[cc][cs][period] = {};
          Object.keys(timetable[className][section][period] || {}).forEach(day => {
            cleaned[cc][cs][period][day] = timetable[className][section][period][day];
          });
        });
      });
    });
    return cleaned;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let cleanContent = { ...pageContent };
      if (cleanContent.timetable) cleanContent.timetable = cleanTimetableData(cleanContent.timetable);
      await saveSettings(settings);
      await savePageContent(activePage, cleanContent);
      alert('✅ All changes saved successfully!');
    } catch (error) {
      alert('❌ Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsImageDelete = async (field, imageUrl) => {
    if (!imageUrl) { setSettings(prev => ({ ...prev, [field]: '' })); return; }
    if (window.confirm(`Delete this ${field} permanently?`)) {
      setUploading(true);
      try {
        const { deleteImageFromStorage } = await import('../firebase/firebase-config');
        await deleteImageFromStorage(imageUrl);
        setSettings(prev => ({ ...prev, [field]: '' }));
        alert('✅ Image deleted!');
      } catch (error) { alert('Error: ' + error.message); }
      finally { setUploading(false); }
    }
  };

  const deleteImageFromFirebase = async (imageUrl, fieldName, isSetting = false) => {
    if (!imageUrl) return;
    if (window.confirm('Delete this image permanently?')) {
      setUploading(true);
      try {
        const { deleteImageFromStorage } = await import('../firebase/firebase-config');
        await deleteImageFromStorage(imageUrl);
        if (isSetting) setSettings(prev => ({ ...prev, [fieldName]: '' }));
        else setPageContent(prev => ({ ...prev, [fieldName]: '' }));
        alert('✅ Image deleted!');
      } catch (error) { alert('Error: ' + error.message); }
      finally { setUploading(false); }
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    setUploading(true);
    setUploadingField(field);
    try {
      const { uploadImage } = await import('../firebase/firebase-config');
      const url = await uploadImage(file, `${activePage}/${field}_${Date.now()}`);
      setPageContent(prev => ({ ...prev, [field]: url }));
      alert(`✅ ${field} uploaded!`);
    } catch (error) { alert('Error: ' + error.message); }
    finally { setUploading(false); setUploadingField(''); }
  };

  const handleSettingsImageUpload = async (field, file) => {
    if (!file) return;
    setUploading(true);
    setUploadingField(field);
    try {
      const { uploadImage } = await import('../firebase/firebase-config');
      const url = await uploadImage(file, `settings/${field}_${Date.now()}`);
      setSettings(prev => ({ ...prev, [field]: url }));
      alert(`✅ ${field} uploaded!`);
    } catch (error) { alert('Error: ' + error.message); }
    finally { setUploading(false); setUploadingField(''); }
  };

  const handleRowImageUpload = async (tableName, rowId, field, file) => {
    if (!file) return;
    setUploading(true);
    setUploadingField(`${tableName}_${rowId}_${field}`);
    try {
      const { uploadImage } = await import('../firebase/firebase-config');
      const url = await uploadImage(file, `${activePage}/${tableName}_${rowId}_${field}_${Date.now()}`);
      updateTableRow(tableName, rowId, field, url);
      alert(`✅ Image uploaded!`);
    } catch (error) { alert('Error: ' + error.message); }
    finally { setUploading(false); setUploadingField(''); }
  };

  const handleFileUpload = async (tableName, rowId, file, category = 'documents') => {
    if (!file) return;
    setUploading(true);
    setUploadingField(`file_${rowId}`);
    try {
      const { uploadFile } = await import('../firebase/firebase-config');
      const fileData = await uploadFile(file, `${activePage}/${category}_${rowId}_${Date.now()}`);
      updateTableRow(tableName, rowId, 'file', fileData);
      updateTableRow(tableName, rowId, 'fileName', file.name);
      alert(`✅ File uploaded!`);
    } catch (error) { alert('Error: ' + error.message); }
    finally { setUploading(false); setUploadingField(''); }
  };

  const addTableRow = (tableName, defaultRow = {}) => {
    const currentRows = pageContent[tableName] || [];
    setPageContent({ ...pageContent, [tableName]: [...currentRows, { id: Date.now(), ...defaultRow }] });
  };

  const deleteTableRow = (tableName, rowId) => {
    if (window.confirm('Delete this item?')) {
      const newRows = (pageContent[tableName] || []).filter(row => row.id !== rowId);
      setPageContent({ ...pageContent, [tableName]: newRows });
    }
  };

  const updateTableRow = (tableName, rowId, field, value) => {
    const newRows = (pageContent[tableName] || []).map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    );
    setPageContent({ ...pageContent, [tableName]: newRows });
  };

  const createNewPage = () => {
    if (!newPageName.trim()) return;
    const pageId = newPageName.toLowerCase().replace(/\s+/g, '');
    setPages({ ...pages, [pageId]: { name: newPageName, icon: '📄', description: 'Custom page' } });
    setActivePage(pageId);
    setPageContent({ title: newPageName, content: '' });
    setShowNewPageModal(false);
    setNewPageName('');
    alert(`Page "${newPageName}" created!`);
  };

  const deleteCustomPage = (pageId) => {
    if (window.confirm(`Delete page "${pages[pageId]?.name}"?`)) {
      const newPages = { ...pages };
      delete newPages[pageId];
      setPages(newPages);
      if (activePage === pageId) setActivePage('home');
      alert('Page deleted');
    }
  };

  const renderPageEditor = () => {
    const editorProps = {
      pageContent,
      setPageContent,
      uploading,
      uploadingField,
      handleImageUpload,
      handleFileUpload,
      handleRowImageUpload,
      deleteImageFromFirebase,
      addTableRow,
      deleteTableRow,
      updateTableRow
    };

    switch(activePage) {
      case 'home': return <HomeEditor {...editorProps} />;
      case 'about': return <AboutEditor {...editorProps} />;
      case 'academics': return <AcademicsEditor {...editorProps} />;
      case 'curriculum': return <CurriculumEditor {...editorProps} />;
      case 'calendar': return <CalendarEditor {...editorProps} />;
      case 'syllabus': return <SyllabusEditor {...editorProps} />;
      case 'questionPapers': return <QuestionPapersEditor {...editorProps} />;
      case 'holidayHomework': return <HolidayHomeworkEditor {...editorProps} />;
      case 'timeTable': return <TimeTableEditor {...editorProps} />;
      case 'teachers': return <TeachersEditor {...editorProps} />;
      case 'gallery': return <GalleryEditor {...editorProps} />;
      case 'notices': return <NoticesEditor {...editorProps} />;
      case 'sports': return <SportsEditor {...editorProps} />;
      case 'contact': return <ContactEditor {...editorProps} />;
      case 'mandatoryDisclosure': return <MandatoryDisclosureEditor {...editorProps} />;
      default: return <DefaultEditor {...editorProps} pageName={pages[activePage]?.name || 'Page'} />;
    }
  };

  const renderSettings = () => {
    const settingsProps = {
      settings,
      setSettings,
      uploading,
      uploadingField,
      setUploading,        // ✅ Added this
      setUploadingField,   // ✅ Added this
      handleSettingsImageUpload,
      handleSettingsImageDelete
    };

    switch(activeTab) {
      case 'header': return <HeaderSettings {...settingsProps} />;
      case 'globalBanner': return <GlobalBannerSettings {...settingsProps} />;
      case 'footer': return <FooterSettings {...settingsProps} />;
      case 'colors': return <ColorSettings {...settingsProps} />;
      default: return null;
    }
  };

  if (loading) return <div className="loading">Loading CMS...</div>;

  return (
    <div className="cms-dashboard-full">
      <div className={`cms-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? '◀' : '▶'}</button>
          {sidebarOpen && <h2>MIS CMS</h2>}
        </div>
        <div className="sidebar-nav">
          <div className="nav-group">
            <div className="nav-group-title">📄 PAGES</div>
            {Object.entries(pages).map(([key, page]) => (
              <button key={key} className={`nav-item ${activePage === key ? 'active' : ''}`} onClick={() => { setActivePage(key); setActiveTab(null); }}>
                <span className="nav-icon">{page.icon}</span>{sidebarOpen && <span className="nav-label">{page.name}</span>}
              </button>
            ))}
          </div>
          <div className="nav-group">
            <div className="nav-group-title">⚙️ SETTINGS</div>
            <button className={`nav-item ${activeTab === 'header' ? 'active' : ''}`} onClick={() => { setActiveTab('header'); setActivePage(null); }}>
              <span className="nav-icon">🏠</span>{sidebarOpen && <span>Header</span>}
            </button>
            <button className={`nav-item ${activeTab === 'globalBanner' ? 'active' : ''}`} onClick={() => { setActiveTab('globalBanner'); setActivePage(null); }}>
              <span className="nav-icon">🌐</span>{sidebarOpen && <span>Global Banner</span>}
            </button>
            <button className={`nav-item ${activeTab === 'footer' ? 'active' : ''}`} onClick={() => { setActiveTab('footer'); setActivePage(null); }}>
              <span className="nav-icon">🦶</span>{sidebarOpen && <span>Footer</span>}
            </button>
            <button className={`nav-item ${activeTab === 'colors' ? 'active' : ''}`} onClick={() => { setActiveTab('colors'); setActivePage(null); }}>
              <span className="nav-icon">🎨</span>{sidebarOpen && <span>Colors</span>}
            </button>
          </div>
          <div className="nav-group">
            <div className="nav-group-title">➕ CUSTOM PAGES</div>
            <button className="nav-item add-page-btn" onClick={() => setShowNewPageModal(true)}>
              <span className="nav-icon">➕</span>{sidebarOpen && <span>Create New Page</span>}
            </button>
            {Object.entries(pages).filter(([key]) => !['home', 'about', 'academics', 'curriculum', 'calendar', 'syllabus', 'questionPapers', 'holidayHomework', 'timeTable', 'teachers', 'gallery', 'notices', 'sports', 'contact', 'mandatoryDisclosure'].includes(key)).map(([key, page]) => (
              <div key={key} style={{display:'flex', alignItems:'center'}}>
                <button className={`nav-item ${activePage === key ? 'active' : ''}`} onClick={() => { setActivePage(key); setActiveTab(null); }} style={{flex:1}}>
                  <span className="nav-icon">{page.icon}</span>{sidebarOpen && <span className="nav-label">{page.name}</span>}
                </button>
                {sidebarOpen && <button className="remove-btn" style={{padding:'4px 8px', marginRight:'8px'}} onClick={() => deleteCustomPage(key)}>🗑️</button>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cms-main">
        <div className="cms-header">
          <div>
            <h1>{activePage ? (pages[activePage]?.name || 'Page Editor') : (activeTab === 'globalBanner' ? 'Global Banner' : 'Settings')}</h1>
            <p>{activePage ? pages[activePage]?.description : 'Customize your school website'}</p>
          </div>
          <button className="save-all-btn" onClick={handleSave} disabled={saving || uploading}>
            {saving ? '💾 Saving...' : uploading ? '⏳ Uploading...' : '💾 Save'}
          </button>
        </div>
        <div className="cms-content">
          {activePage ? renderPageEditor() : renderSettings()}
        </div>
      </div>

      {showNewPageModal && (
        <div className="modal-overlay" onClick={() => setShowNewPageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 Create New Page</h3>
              <button className="close-btn" onClick={() => setShowNewPageModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Page Name</label>
                <input type="text" value={newPageName} onChange={(e) => setNewPageName(e.target.value)} placeholder="e.g., Alumni, Careers" className="form-input" autoFocus />
                <small>URL: /{newPageName.toLowerCase().replace(/\s+/g, '-')}</small>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowNewPageModal(false)}>Cancel</button>
                <button className="create-btn" onClick={createNewPage}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;